/* eslint-disable @typescript-eslint/no-unused-vars */
import { createMachine, assign, DoneInvokeEvent } from 'xstate';

import { TokenizationError, CreditCardFields } from '@chargify/payments-sdk';
import { AddressFields } from '@chargify/address';

import {
  assignCreatedProfile,
  serializePaymentProfilesSubscriptions,
  resetFieldError,
  setPaymentProfileIdForReplacement,
} from './actions';

import { Context, Events, FieldError } from './types';

export const initialContext: Context = {
  token: null,
  tokenizationError: null,
  requiredFields: null,
  validationSchema: null,
  createdProfile: null,
  paymentMethod: {
    errors: {},
    values: {
      firstName: '',
      lastName: '',
    },
  },
  billingAddress: {
    errors: {},
    values: {
      address: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    },
  },
  paymentProfiles: {
    idsForReplacement: [],
    subscriptions: {
      failed: null,
      success: null,
    },
  },
};

const createPaymentProfile = createMachine<Context, Events>(
  {
    context: initialContext,
    initial: 'idle',
    predictableActionArguments: true,
    on: {
      UPDATE_FIELD: {
        actions: [
          assign((ctx, event) => ({
            [event.form]: {
              ...ctx[event.form],
              values: {
                ...ctx[event.form].values,
                [event.field]: event.value,
              },
            },
          })),
        ],
      },
      RESET_FIELD_ERROR: {
        actions: ['resetFieldError'],
      },
      SET_FIELD_ERROR: {
        actions: [
          assign((ctx, event) => {
            const { error, field, form } = event;
            return {
              [form]: {
                ...ctx[form],
                errors: {
                  ...ctx[form].errors,
                  [field]: error,
                },
              },
            };
          }),
        ],
      },
    },
    states: {
      idle: {
        on: {
          SET_PAYMENT_PROFILES_ID_FOR_REPLACEMENT: {
            actions: 'setPaymentProfileIdForReplacement',
          },
          SAVE_PAYMENT_METHOD: {
            target: 'validatePaymentMethod',
          },
        },
      },
      validatePaymentMethod: {
        entry: [
          assign((ctx) => ({
            tokenizationError: null,
            billingAddress: {
              ...ctx.billingAddress,
              errors: {},
            },
          })),
        ],
        invoke: {
          id: 'validatePaymentMethod',
          src: 'validatePaymentMethod',
          onDone: {
            target: 'tokenizePaymentMethod',
          },
          onError: {
            target: 'validationError',
            actions: [
              assign(
                (
                  ctx,
                  event: DoneInvokeEvent<{
                    errors: {
                      billingAddress: {
                        [key in keyof AddressFields]?: FieldError;
                      };
                      paymentMethod: {
                        [key in keyof CreditCardFields]?: FieldError;
                      };
                    };
                  }>
                ) => ({
                  billingAddress: {
                    ...ctx.billingAddress,
                    errors: event.data.errors.billingAddress,
                  },
                  paymentMethod: {
                    ...ctx.paymentMethod,
                    errors: event.data.errors.paymentMethod,
                  },
                })
              ),
            ],
          },
        },
      },
      tokenizePaymentMethod: {
        invoke: {
          id: 'tokenizePaymentMethod',
          src: 'tokenizePaymentMethod',
          onDone: {
            target: 'createPaymentProfile',
            actions: [
              assign((_ctx, event: DoneInvokeEvent<string>) => ({
                token: event.data,
              })),
            ],
          },
          onError: {
            target: 'tokenizationError',
            actions: [
              assign((_ctx, event: DoneInvokeEvent<TokenizationError>) => ({
                tokenizationError: event.data,
              })),
            ],
          },
        },
      },
      createPaymentProfile: {
        invoke: {
          id: 'createProfile',
          src: 'createPaymentProfile',
          onDone: [
            {
              cond: 'hasPaymentProfilesIdsForReplacement',
              target: 'replacePaymentProfiles',
              actions: 'assignCreatedProfile',
            },
            {
              target: 'success',
              actions: [
                'assignCreatedProfile',
                'onCreateProfileSuccess',
                assign((_ctx, event) => ({
                  createdProfile: event.data,
                })),
              ],
            },
          ],
          onError: {
            target: 'createProfileError',
          },
        },
      },
      replacePaymentProfiles: {
        invoke: {
          id: 'replacePaymentProfiles',
          src: 'replacePaymentProfiles',
          onDone: {
            target: 'success',
            actions: [
              'onReplacePaymentProfilesSuccess',
              'onCreateProfileSuccess',
            ],
          },
          onError: {
            target: 'replacePaymentProfilesError',
            actions: 'serializePaymentProfilesSubscriptions',
          },
        },
      },
      validationError: {
        on: {
          SAVE_PAYMENT_METHOD: {
            target: 'validatePaymentMethod',
          },
        },
      },
      replacePaymentProfilesError: {
        on: {
          RETRY_REPLACE_PAYMENT_PROFILES: {
            target: 'replacePaymentProfiles',
          },
        },
      },
      createProfileError: {
        on: {
          SAVE_PAYMENT_METHOD: {
            target: 'validatePaymentMethod',
          },
        },
      },
      tokenizationError: {
        on: {
          SAVE_PAYMENT_METHOD: {
            target: 'validatePaymentMethod',
          },
        },
      },
      success: {
        type: 'final',
      },
    },
  },
  {
    actions: {
      serializePaymentProfilesSubscriptions,
      setPaymentProfileIdForReplacement,
      resetFieldError,
      assignCreatedProfile,
    },
    guards: {
      hasPaymentProfilesIdsForReplacement: (ctx) =>
        ctx.paymentProfiles.idsForReplacement.length > 0,
    },
  }
);

export { createPaymentProfile };
